import { useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { MessagingApi } from 'lib/api/messaging';
import {
  TERMINAL_STATUSES,
  POLLING_INTERVAL_MS,
  POLLING_MAX_ATTEMPTS,
} from './whatsapp-status.constants';

export type WhatsappStatusMap = Record<
  string,
  { status: string | null; updatedAt: string | null }
>;

export function useWhatsappPolling() {
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useRef(true);

  const [sendingWhatsapp, setSendingWhatsapp] = useState<
    Record<string, boolean>
  >({});
  const [whatsappStatuses, setWhatsappStatuses] = useState<WhatsappStatusMap>(
    {},
  );

  const showWhatsappStatusToast = (status: string, phone: string) => {
    const snackbarOptions = { autoHideDuration: 10000 };
    switch (status) {
      case 'DELIVERED':
      case 'READ':
        enqueueSnackbar(
          `Mensagem entregue ao WhatsApp ${phone} do candidato!`,
          {
            variant: 'success',
            ...snackbarOptions,
          },
        );
        break;
      case 'SENT':
      case 'QUEUED':
        enqueueSnackbar(
          `Mensagem enviada para a operadora. Aguardando confirmação de entrega.`,
          { variant: 'warning', ...snackbarOptions },
        );
        break;
      case 'FAILED':
      case 'UNDELIVERED':
        enqueueSnackbar(
          `Falha no envio para ${phone}. Verifique o número do candidato ou acione o suporte.`,
          { variant: 'error', ...snackbarOptions },
        );
        break;
      default:
        enqueueSnackbar(
          `Não foi possível confirmar o status. Se o problema persistir, acione o suporte.`,
          { variant: 'warning', ...snackbarOptions },
        );
    }
  };

  const pollWhatsappStatus = async (
    analystRequestId: string,
    messageType: string,
    phone: string,
    expectedSid?: string,
  ) => {
    const statusKey = `${analystRequestId}:${messageType}`;
    let lastResult: { status: string | null; updatedAt: string | null } | null =
      null;

    for (let attempt = 0; attempt < POLLING_MAX_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL_MS));

      if (!isMountedRef.current) return;

      try {
        const result = await MessagingApi.getWhatsappStatus(
          analystRequestId,
          messageType,
        );
        if (!isMountedRef.current) return;

        if (
          expectedSid &&
          result.messageSid &&
          result.messageSid !== expectedSid
        ) {
          continue;
        }

        if (result.messageStatus) {
          lastResult = {
            status: result.messageStatus,
            updatedAt: result.updatedAt ?? null,
          };
        }

        if (
          result.messageStatus &&
          TERMINAL_STATUSES.includes(result.messageStatus)
        ) {
          setWhatsappStatuses((prev) => ({
            ...prev,
            [statusKey]: {
              status: result.messageStatus,
              updatedAt: result.updatedAt ?? null,
            },
          }));
          showWhatsappStatusToast(result.messageStatus, phone);
          return;
        }
      } catch {
        // continua tentando
      }
    }

    if (!isMountedRef.current) return;

    if (lastResult?.status) {
      const finalResult = lastResult;
      setWhatsappStatuses((prev) => ({
        ...prev,
        [statusKey]: {
          status: finalResult.status,
          updatedAt: finalResult.updatedAt,
        },
      }));
    }

    enqueueSnackbar(
      `Mensagem enviada para a operadora. Aguardando confirmação de entrega.`,
      {
        variant: 'warning',
        autoHideDuration: 10000,
      },
    );
  };

  const startSending = (key: string) =>
    setSendingWhatsapp((prev) => ({ ...prev, [key]: true }));

  const stopSending = (key: string) =>
    setSendingWhatsapp((prev) => ({ ...prev, [key]: false }));

  return {
    isMountedRef,
    sendingWhatsapp,
    whatsappStatuses,
    setWhatsappStatuses,
    pollWhatsappStatus,
    showWhatsappStatusToast,
    startSending,
    stopSending,
  };
}
